import {
  Backdrop,
  Box,
  CircularProgress,
  FormHelperText,
  Grid,
  Link,
  Typography,
  capitalize,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { CheckoutContext } from "contexts/Checkout";
import { SplashPage, SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { AddOnItemName } from "schemas/dashboard";
import { DisplayName, Plans } from "schemas/payments";

import StyledLargeButton from "components/Styled/Button/StyledLargeButton";
import { VA_MONEY_BACK_GUARANTEE } from "constants/influencer";
import { useSubscription } from "utils/useSubscription";

import { StripeCustomer } from "../schema";
import { useCheckout } from "../useCheckout";

interface Props {
  onConfirm: () => void;
  confirmLoading: boolean;
  customer: StripeCustomer;
  noNeedsToPay: boolean;
  plan: Plans | AddOnItemName;
}

const ConfirmPaymentForm = ({
  onConfirm,
  confirmLoading,
  customer,
  noNeedsToPay,
  plan,
}: Props) => {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { plans } = useContext(SubscriptionContext);
  const { handleManagePlan, checkoutLoading } = useSubscription();
  const { splashPageSource, addOnItems } = useContext(CheckoutContext);
  const { isVaMoneyBack } = useCheckout();

  const [terms, setTerms] = useState<string>("");

  const redirectToBillingPay = () => {
    handleManagePlan(plan);
  };

  const getCardIcon = () => {
    if (customer?.payment_method?.card?.brand === "visa") {
      return "fa-brands fa-cc-visa";
    } else if (customer?.payment_method?.card?.brand === "mastercard") {
      return "fa-brands fa-cc-mastercard";
    } else {
      return "fa-solid fa-credit-card";
    }
  };

  const displayCard = () => {
    return (
      <Box sx={{ mb: 1 }}>
        <Typography gutterBottom>
          <strong> Your current payment method</strong>
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Box>
            <Box component="i" className={getCardIcon()} sx={{ mr: 1 }} />
            {capitalize(customer?.payment_method?.card?.brand || "")} ****
            {customer?.payment_method?.card?.last4}
          </Box>

          <Box>
            Expires {customer?.payment_method?.card?.exp_month}/
            {customer?.payment_method?.card?.exp_year}
          </Box>
        </Box>
      </Box>
    );
  };

  useEffect(() => {
    if (isVaMoneyBack) {
      setTerms(VA_MONEY_BACK_GUARANTEE);
    } else if (plans && plan && plans[plan as Plans]?.terms_and_conditions) {
      setTerms(plans[plan as Plans]?.terms_and_conditions || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan]);

  return (
    <>
      <Grid item xs={12} sx={{ mb: 3 }}>
        {checkoutLoading && (
          <Backdrop
            sx={{
              backgroundColor: "#fff",
              zIndex: 9999,
            }}
            open={checkoutLoading === plan}
          >
            <CircularProgress />
          </Backdrop>
        )}

        <Typography
          variant="h4"
          gutterBottom
          sx={{ fontSize: { xs: 20, md: 25 } }}
        >
          Confirm Your Updates
        </Typography>

        {noNeedsToPay ? (
          <Typography>
            Click <strong>Confirm</strong> to upgrade to your new subscription.
            You do not need to pay anything at this time.
          </Typography>
        ) : (
          <Box>
            {customer?.payment_method && displayCard()}
            We will be charging your payment form on file displayed above with
            the amount on the invoice. <br />
            If you want to update your default payment method, click{" "}
            <Link
              sx={{ textDecoration: "none", cursor: "pointer" }}
              onClick={redirectToBillingPay}
            >
              <strong>here</strong>
            </Link>
          </Box>
        )}

        <Grid container gap={1} alignItems={"center"}>
          <StyledLargeButton
            loading={confirmLoading}
            styles={{ my: 3 }}
            onClick={onConfirm}
            disabled={
              splashPageSource === SplashPage.va_bundled &&
              addOnItems.length === 0
            }
            fullWidth={isMobileScreen}
          >
            Confirm
          </StyledLargeButton>
        </Grid>

        <FormHelperText sx={{ maxWidth: 700 }}>
          By confirming your new subscription, you agree to{" "}
          {terms ? (
            <>
              <Link href={terms} target="_blank" sx={{ cursor: "pointer" }}>
                <strong>{DisplayName[plan]} Terms</strong>
              </Link>{" "}
              and
            </>
          ) : (
            ""
          )}{" "}
          allow Bento to charge you in accordance with{" "}
          <Link
            href="https://www.onbento.com/terms-of-services"
            target="_blank"
            sx={{ cursor: "pointer" }}
          >
            <strong>Bento's Terms of Service</strong>
          </Link>{" "}
          and{" "}
          <Link
            sx={{ cursor: "pointer" }}
            href="https://www.onbento.com/privacy-policy"
            target="_blank"
          >
            <strong>Privacy Policy</strong>
          </Link>
          .
        </FormHelperText>
      </Grid>
    </>
  );
};

export default ConfirmPaymentForm;
